import type { Article } from "@/model/backend/article"
import { computed, type Ref } from "vue"

export interface PackageCombinationItem {
    size: number
    count: number
}

/**
 * Composable for calculating how packages will be combined to match the requested weight.
 * Finds an exact combination with the fewest packages (unbounded per package size).
 *
 * @param article - The article (must have isPreparedType and batchPreparationPackageAmount)
 * @param totalWeight - The total amount to fulfill, in the article's unit (gram or milliliter)
 */
export function usePackageCombination(article: Ref<Article>, totalWeight: Ref<number>) {
    const availablePackageSizes = computed<number[]>(() => {
        if (!article.value.isPreparedType) {
            return []
        }

        const rawSizes = article.value.availableBatchPreparationPackageAmounts ?? []

        return [...new Set(rawSizes)].filter(size => Number.isInteger(size) && size > 0).sort((a, b) => b - a)
    })

    /**
     * The minimum package size available for the article
     */
    const minPackageSize = computed(() => {
        if (availablePackageSizes.value.length > 0) {
            return Math.min(...availablePackageSizes.value)
        }
        return 1
    })

    /**
     * Calculates how packages will be combined to match the requested weight.
     * Uses unbounded exact-fit DP and minimizes the number of packages.
     */
    const packageCombination = computed<PackageCombinationItem[] | null>(() => {
        if (!article.value.isPreparedType || availablePackageSizes.value.length === 0) {
            return null
        }

        const targetWeight = Math.floor(totalWeight.value)
        if (!Number.isFinite(targetWeight) || targetWeight <= 0) {
            return null
        }

        const minPackages = Array<number>(targetWeight + 1).fill(Number.POSITIVE_INFINITY)
        const lastPackageSize = Array<number>(targetWeight + 1).fill(0)
        minPackages[0] = 0

        for (let weight = 1; weight <= targetWeight; weight++) {
            for (const size of availablePackageSizes.value) {
                if (size > weight) {
                    continue
                }

                const previousCount = minPackages[weight - size]
                if (!Number.isFinite(previousCount)) {
                    continue
                }

                const candidateCount = previousCount + 1
                // Keep first best candidate to preserve deterministic tie-break:
                // package sizes are iterated descending, so ties prefer larger packages.
                if (candidateCount < minPackages[weight]) {
                    minPackages[weight] = candidateCount
                    lastPackageSize[weight] = size
                }
            }
        }

        if (!Number.isFinite(minPackages[targetWeight])) {
            return null
        }

        const countBySize = new Map<number, number>()
        let remainingWeight = targetWeight
        while (remainingWeight > 0) {
            const size = lastPackageSize[remainingWeight]
            if (!size) {
                return null
            }

            countBySize.set(size, (countBySize.get(size) ?? 0) + 1)
            remainingWeight -= size
        }

        const combination: PackageCombinationItem[] = []
        for (const size of availablePackageSizes.value) {
            const count = countBySize.get(size)
            if (count) {
                combination.push({ size, count })
            }
        }

        return combination
    })

    /**
     * Formats the package combination for display.
     * Returns null if no info should be shown (only minimum size packages used).
     * Shows info when any package larger than the minimum size is used.
     */
    const packageCombinationText = computed<string | null>(() => {
        if (!packageCombination.value || packageCombination.value.length === 0) {
            return null
        }

        // Show info if any package size larger than the minimum is used
        const usesLargerPackage = packageCombination.value.some(p => p.size > minPackageSize.value)
        if (!usesLargerPackage) {
            return null
        }

        // Gebinde sizes are given in the article's unit: gram for flowers, milliliter for xtracts.
        // normalizedPackageUnit is always set, so no fallback is needed.
        const unit = article.value.normalizedPackageUnit

        return packageCombination.value.map(p => `${p.count}x ${p.size} ${unit}`).join(" + ")
    })

    return {
        minPackageSize,
        packageCombination,
        packageCombinationText,
    }
}
